import { useState } from 'react';
import { formatRelativeTime } from '@/lib/utils';
import { useUpdateArticleComment, useDeleteArticleComment } from '@/queries/articles';
import { useAuth } from '@/contexts/AuthContext';
import styles from './CommentItem.module.scss';
import type { ArticleComment } from '@/types/article';

interface CommentItemProps {
  comment: ArticleComment;
  articleId: string;
}

export function CommentItem({ comment, articleId }: CommentItemProps) {
  const { user } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(comment.content);

  const updateComment = useUpdateArticleComment();
  const deleteComment = useDeleteArticleComment();

  const isOwner = user?.uid === comment.user_id;

  const handleSave = () => {
    if (!editContent.trim()) return;

    updateComment.mutate(
      { articleId, commentId: comment.id, content: editContent.trim() },
      {
        onSuccess: () => {
          setIsEditing(false);
        },
      }
    );
  };

  const handleDelete = () => {
    if (confirm('Are you sure you want to delete this comment?')) {
      deleteComment.mutate({ articleId, commentId: comment.id });
    }
  };

  return (
    <div className={styles.comment}>
      <div className={styles.comment__header}>
        <span className={styles.comment__author}>
          {comment.author?.display_name || comment.author?.email || 'Anonymous'}
        </span>
        <span className={styles.comment__date}>
          {formatRelativeTime(comment.created_at)}
          {comment.updated_at !== comment.created_at && ' (edited)'}
        </span>
      </div>

      {isEditing ? (
        <div className={styles.comment__edit}>
          <textarea
            className={styles.comment__editInput}
            value={editContent}
            onChange={(e) => setEditContent(e.target.value)}
            rows={3}
          />
          <div className={styles.comment__editActions}>
            <button
              onClick={handleSave}
              className={styles.comment__saveButton}
              disabled={updateComment.isPending}
            >
              {updateComment.isPending ? 'Saving...' : 'Save'}
            </button>
            <button
              onClick={() => {
                setIsEditing(false);
                setEditContent(comment.content);
              }}
              className={styles.comment__cancelButton}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <p className={styles.comment__content}>{comment.content}</p>
      )}

      {isOwner && !isEditing && (
        <div className={styles.comment__actions}>
          <button
            onClick={() => setIsEditing(true)}
            className={styles.comment__actionButton}
          >
            Edit
          </button>
          <button
            onClick={handleDelete}
            className={`${styles.comment__actionButton} ${styles['comment__actionButton--danger']}`}
            disabled={deleteComment.isPending}
          >
            {deleteComment.isPending ? 'Deleting...' : 'Delete'}
          </button>
        </div>
      )}
    </div>
  );
}
